import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Building, Wind, Sun, Eye, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

const facadeSystems = [
  {
    id: 'curtain-wall-advanced',
    title: 'İleri Teknoloji Giydirme Cephe Sistemleri',
    description: 'Yüksek performanslı, enerji verimli ve estetik giydirme cephe çözümleri.',
    image: IMAGES.CEPHE_GIYDIRME_286,
    features: [
      'Yapısal glazing teknolojisi',
      'Termal break sistemi ile ısı yalıtımı',
      'Deprem dayanımı ve esneklik',
      'Yangın güvenlik sınıfı EI60',
      'Ses yalıtımı Rw 45 dB',
      'Bakım kolaylığı ve uzun ömür'
    ],
    applications: [
      'Gökdelenler ve yüksek binalar',
      'Ticari ve ofis kompleksleri',
      'Hastane ve sağlık tesisleri',
      'Havalimanı ve ulaşım merkezleri'
    ],
    specifications: [
      'Cam kalınlığı: 6-24mm',
      'Rüzgar yükü: 3.0 kN/m²',
      'Hava geçirgenliği: A4',
      'Su geçirgenliği: E1800'
    ]
  },
  {
    id: 'window-door-systems',
    title: 'Yüksek Performanslı Pencere ve Kapı Sistemleri',
    description: 'Enerji verimliliği ve konfor odaklı pencere ve kapı çözümleri.',
    image: IMAGES.DO_RAMA_112_307,
    features: [
      'Üstün ısı yalıtımı (Uf ≤ 1.0 W/m²K)',
      'Çok odacıklı profil yapısı',
      'Üçlü cam seçenekleri',
      'Otomatik kilit sistemleri',
      'Anti-hırsızlık güvenlik',
      'Çeşitli renk ve doku seçenekleri'
    ],
    applications: [
      'Lüks konut projeleri',
      'Ofis ve ticari binalar',
      'Otel ve turizm tesisleri',
      'Eğitim kurumları'
    ],
    specifications: [
      'Profil derinliği: 70-85mm',
      'Cam kalınlığı: 24-44mm',
      'Hava sızdırmazlığı: A4',
      'Su sızdırmazlığı: E1800'
    ]
  },
  {
    id: 'composite-panels',
    title: 'Alüminyum Kompozit Panel Sistemleri',
    description: 'Hafif, dayanıklı ve estetik cephe kaplama çözümleri.',
    image: IMAGES.COMPOSITE_308,
    features: [
      'Hafif yapı (4-6 kg/m²)',
      'Yüksek düzlük ve stabilite',
      'Yangın dayanımı A2-s1,d0',
      'UV dayanımı ve renk sabitliği',
      'Kolay işlenebilirlik',
      'Geniş renk ve desen seçenekleri'
    ],
    applications: [
      'Modern ofis binaları',
      'Alışveriş merkezleri',
      'Endüstriyel tesisler',
      'Spor ve kültür merkezleri'
    ],
    specifications: [
      'Panel kalınlığı: 3-6mm',
      'Boyutlar: 1220x2440mm - 1500x4000mm',
      'Çekirdek: PE/FR çekirdek',
      'Yüzey: PVDF/Polyester kaplama'
    ]
  },
  {
    id: 'louvre-profiles',
    title: 'Louvre Profilleri',
    description: 'Güneş kontrolü, doğal havalandırma ve estetik cephe tasarımını bir arada sunan mimari louvre (güneş kırıcı kanat) profil sistemleri.',
    image: IMAGES.EKRAN_GORUNTUSU_LOUVRE,
    features: [
      'Sabit ve ayarlanabilir kanat seçenekleri',
      'Yüksek mukavemetli 6000 serisi alaşım',
      'Toz boya veya anodize yüzey',
      'Özel kanat genişliği ve derinliği',
      'Motor tahrikli otomatik sistemler',
      'Yüksek rüzgar yükü dayanımı'
    ],
    applications: [
      'Ofis ve ticari bina cepheleri',
      'Otopark ve havalandırmalı yapılar',
      'Güneş kırıcı ve solar ekranlama',
      'Dekoratif mimari kaplama'
    ],
    specifications: [
      'Kanat genişliği: 100–300mm',
      'Alaşım: 6063-T5 / 6061-T6',
      'Yüzey: Toz boya / Anodize',
      'Rüzgar dayanımı: 3.0 kN/m²\'ye kadar'
    ]
  }
];

export default function FacadeSystems() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.FACTORY_FLOOR_1} 
            alt="Cephe ve Doğrama Sistemleri" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/60" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <Link 
              to={ROUTE_PATHS.HOME} 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Ana Sayfaya Dön
            </Link>
            
            <h1 className="text-5xl font-bold text-white mb-6">
              Cephe ve Doğrama Sistemleri
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Modern binalar için estetik cephe kaplamaları ve doğrama çözümleri. 
              Enerji verimliliği, güvenlik ve tasarım mükemmelliğini bir araya getiren sistemlerimiz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-24"
          >
            {facadeSystems.map((product, index) => (
              <motion.div
                key={product.id}
                variants={staggerItem}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="relative rounded-2xl overflow-hidden group">
                    <img 
                      src={product.image} 
                      alt={product.title}
                      className={`w-full ${product.id === 'composite-panels' ? 'h-[500px] object-cover object-center' : 'h-[400px] object-cover'} group-hover:scale-105 transition-transform duration-500`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <h2 className="text-3xl font-bold text-primary mb-4">
                    {product.title}
                  </h2>
                  <p className="text-muted-foreground mb-6 text-lg">
                    {product.description}
                  </p>
                  
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <Building className="w-5 h-5 text-primary" />
                      Sistem Özellikleri
                    </h3>
                    <ul className="space-y-2">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <Eye className="w-5 h-5 text-primary" />
                      Uygulama Alanları
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {product.applications.map((app, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                        >
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <Layers className="w-5 h-5 text-primary" />
                      Teknik Özellikler
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {product.specifications.map((spec, idx) => (
                        <div key={idx} className="text-sm text-muted-foreground bg-muted/30 px-3 py-2 rounded">
                          {spec}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-primary mb-6">
              Projeniz İçin Cephe Çözümü mü Arıyorsunuz?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Mimarlık ekibimizle birlikte çalışarak, projenizin estetik ve teknik gereksinimlerine 
              uygun cephe ve doğrama sistemleri tasarlıyoruz.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="px-8">
                <Link to={`${ROUTE_PATHS.CONTACT}#teklif-formu`}>
                  Proje Danışmanlığı İsteyin
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-8">
                <Link to={ROUTE_PATHS.PRODUCTS}>
                  Tüm Ürünleri Görün
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}